import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, CalendarRange, Users, FileText, Tags, Settings as SettingsIcon,
  Sliders, ListChecks, ScrollText, LogOut, Menu, X, User, CreditCard, AlertTriangle, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const NAV_TOP = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/bookings/calendar", label: "Calendar", icon: CalendarRange, testid: "nav-calendar" },
  { to: "/app/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/app/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
];

const NAV_SETTINGS = [
  { to: "/app/event-types", label: "Event Types", icon: Tags, testid: "nav-event-types" },
  { to: "/app/custom-fields", label: "Custom Fields", icon: Sliders, testid: "nav-custom-fields", adminOnly: true },
  { to: "/app/team", label: "Team", icon: ListChecks, testid: "nav-team", adminOnly: true },
  { to: "/app/subscription", label: "Subscription", icon: CreditCard, testid: "nav-subscription", adminOnly: true },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", adminOnly: true },
  { to: "/app/profile", label: "My Profile", icon: User, testid: "nav-profile" },
];

function TrialBanner({ company }) {
  if (!company || company.subscription_status !== "trialing" || !company.trial_ends_at) return null;
  const ms = new Date(company.trial_ends_at) - new Date();
  const days = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  if (days > 4) return null;
  return (
    <div className="bg-[#FEF6E4] border-b border-[#F2D9A6] text-[#7C4A03] px-4 md:px-8 py-2 text-sm flex items-center gap-2" data-testid="trial-banner">
      <AlertTriangle size={14} />
      <span>You're on a free trial — {days} day{days !== 1 ? "s" : ""} left.</span>
      <Link to="/app/subscription" className="ml-auto underline">Upgrade</Link>
    </div>
  );
}

export default function CompanyLayout() {
  const { user, company, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const topItems = NAV_TOP.filter((n) => !n.adminOnly || isAdmin);
  const settingsItems = NAV_SETTINGS.filter((n) => !n.adminOnly || isAdmin);

  return (
    <div className="min-h-screen flex bg-[#F5F4F0]">
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static inset-y-0 left-0 z-40 w-64 bg-[#121212] text-zinc-300 flex flex-col transition-transform thin-scroll`}
        data-testid="company-sidebar"
      >
        <div className="px-6 h-16 flex items-center justify-between border-b border-white/5">
           <Link to="/app" className="flex items-center hover:opacity-80 transition-opacity">
            <img 
              src="/planniq_logo_white.png" 
              alt="EventFlow" 
              className="h-10 w-auto"
            />
          </Link>
          <button className="md:hidden text-zinc-300" onClick={() => setOpen(false)} data-testid="sidebar-close-btn" aria-label="Close sidebar">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto thin-scroll">
          {topItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-[#27272A] text-white" : "text-zinc-400 hover:bg-[#1f1f22] hover:text-white"
                }`
              }
              onClick={() => setOpen(false)}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
          
          <button
            data-testid="nav-settings-toggle"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm w-full text-zinc-400 hover:bg-[#1f1f22] hover:text-white transition-colors"
            onClick={() => setSettingsOpen(!settingsOpen)}
          >
            <SettingsIcon size={18} />
            <span className="flex-1 text-left">Settings</span>
            <ChevronDown size={16} className={`transition-transform ${settingsOpen ? "rotate-180" : ""}`} />
          </button>
          
          {settingsOpen && (
            <div className="pl-6 space-y-1">
              {settingsItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                      isActive ? "bg-[#27272A] text-white" : "text-zinc-400 hover:bg-[#1f1f22] hover:text-white"
                    }`
                  }
                  onClick={() => setOpen(false)}
                >
                  <item.icon size={18} />
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-white/5">
          <div className="px-2 py-3 text-xs">
            <div className="text-zinc-400 truncate">{company?.name || "Workspace"}</div>
            <div className="text-zinc-500 truncate">{user?.email}</div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-zinc-300 hover:bg-[#1f1f22] hover:text-white"
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-[#F5F4F0]/80 backdrop-blur-xl border-b border-black/5 flex items-center px-4 md:px-8 justify-between">
          <button className="md:hidden p-2 rounded-md hover:bg-black/5" onClick={() => setOpen(true)} data-testid="sidebar-open-btn" aria-label="Open sidebar">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-3 ml-auto">
            <div className="md:hidden text-xs bg-[#FEF6E4] border border-[#F2D9A6] text-[#7C4A03] px-3 py-1 rounded">Please use mobile.planniq.in for better experience in mobile</div>
            <span className="hidden sm:inline text-sm text-zinc-600">{user?.name}</span>
            <Link to="/app/profile">
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#1A1A1A] text-white text-sm font-medium hover:bg-black" data-testid="user-avatar">
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </span>
            </Link>
          </div>
        </header>
        <TrialBanner company={company} />
        <main className="flex-1 p-4 md:p-8 thin-scroll overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
